import { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { ModalState } from '../../app/AppRoutes';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { todayIso } from '../../data/store/store';
import type { BudgetMember, Category, Subcategory, Transaction } from '../../domain/model';
import { formatDayMonth } from '../../i18n/format';
import { parseAmount, type ParsedAmount } from '../../i18n/parseAmount';
import { AmountInput, BottomSheet, Button, TextField, useFormat, useToast } from '../../ui';
import { categoryLabel } from '../categories/labels';
import { amountText, ConvertedAmount, PaidInField, useConversion } from './paidIn';
import { ReadOnlyNotice, useReadOnly } from '../../premium/ReadOnlyNotice';
import { PeopleFields, useMemberName } from './people';

interface Loaded {
  entry: Transaction;
  members: BudgetMember[];
  everyone: Map<string, BudgetMember>;
  category?: Category;
  sub?: Subcategory;
}

/**
 * One entry, opened from a list (/entry/:id over the screen it came from):
 * amount, date, note, who paid and whose it is can change; who added it
 * cannot. Deleting offers Undo, like adding does.
 */
export function EntrySheet() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store, identity, activeSpace, language, notifyDataChanged } = useServices();
  const readOnly = useReadOnly(activeSpace?.id);
  const { money, formatLocale } = useFormat();
  const toast = useToast();
  const name = useMemberName();
  const [loaded, setLoaded] = useState<Loaded | null | undefined>(undefined);
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<ParsedAmount>(null);
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [paidBy, setPaidBy] = useState('');
  const [belongsTo, setBelongsTo] = useState('');
  /** The currency it was paid in; the budget's own unless it was paid abroad. */
  const [paidIn, setPaidIn] = useState('');
  const [conversionTouched, setConversionTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const openedOverScreen = Boolean((location.state as ModalState)?.background);
  const close = () => (openedOverScreen ? navigate(-1) : navigate('/', { replace: true }));
  const budgetCurrency = loaded?.entry.currency ?? 'USD';
  const conversion = useConversion(typeof parsed === 'number' ? parsed : null, paidIn || budgetCurrency, budgetCurrency, date);
  const { type: keepConverted } = conversion;

  useEffect(() => {
    let live = true;
    void (async () => {
      const entry = await store.transactions.get(id);
      if (!entry) return live && setLoaded(null);
      const [all, category, sub] = await Promise.all([
        store.members.list({ spaceId: entry.spaceId }, { includeDeleted: true, orderBy: 'created_at' }),
        store.categories.get(entry.categoryId, { includeDeleted: true }),
        entry.subcategoryId ? store.subcategories.get(entry.subcategoryId, { includeDeleted: true }) : Promise.resolve(undefined),
      ]);
      if (!live) return;
      setLoaded({ entry, members: all.filter((m) => m.deletedAt === null), everyone: new Map(all.map((m) => [m.id, m])), category, sub });
      // Paid abroad: edit what was paid, next to the value it was saved with.
      const original = entry.originalCurrency && entry.originalAmountMinor ? { minor: entry.originalAmountMinor, currency: entry.originalCurrency } : null;
      setPaidIn(original?.currency ?? entry.currency);
      setText(amountText(original?.minor ?? entry.amountMinor, original?.currency ?? entry.currency, formatLocale));
      setParsed(original?.minor ?? entry.amountMinor);
      if (original) keepConverted(amountText(entry.amountMinor, entry.currency, formatLocale), entry.amountMinor);
      setDate(entry.date);
      setNote(entry.notes ?? '');
      setPaidBy(entry.paidBy);
      setBelongsTo(entry.ownership === 'shared' ? 'shared' : (entry.ownerMemberId ?? 'shared'));
    })();
    return () => {
      live = false;
    };
  }, [store, id, formatLocale, keepConverted]);

  if (loaded === undefined) return null;
  if (loaded === null) {
    return (
      <BottomSheet open onClose={close} title={t('entry.goneTitle')}>
        <p className="account-note">{t('entry.goneBody')}</p>
      </BottomSheet>
    );
  }

  const { entry, members, everyone } = loaded;
  const me = members.find((m) => m.userId === identity.session.userId);
  const label = categoryLabel(t, loaded.sub ?? loaded.category);
  const amountError = parsed === 'invalid' || parsed === 0 || parsed === null ? t('quickAdd.invalid') : undefined;
  const addedBy = t('entry.addedBy', { name: name(everyone.get(entry.addedBy)), date: formatDayMonth(todayIso(entry.createdAt), language) });

  async function save() {
    if (typeof parsed !== 'number' || parsed <= 0 || saving) {
      amountRef.current?.focus();
      return;
    }
    const foreign = conversion.foreign;
    if (foreign && conversion.result.amountMinor === null) {
      setConversionTouched(true);
      return;
    }
    setSaving(true);
    setFailed(false);
    try {
      await store.transactions.update(
        entry.id,
        {
          amountMinor: foreign ? conversion.result.amountMinor! : parsed,
          originalAmountMinor: foreign ? parsed : null,
          originalCurrency: foreign ? paidIn : null,
          date: date || entry.date,
          notes: note.trim() || null,
          paidBy: paidBy || entry.paidBy,
          ownership: belongsTo === 'shared' ? 'shared' : 'personal',
          ownerMemberId: belongsTo === 'shared' ? null : belongsTo,
        },
        me?.id ?? null,
      );
      notifyDataChanged();
      close();
      toast.show({ message: t('entry.saved', { label }) });
    } catch (e) {
      console.error('[entry]', e);
      setFailed(true);
      setSaving(false);
    }
  }

  async function remove() {
    await store.transactions.remove(entry.id, me?.id ?? null);
    notifyDataChanged();
    close();
    toast.show({
      message: t('entry.deleted', { label, amount: money(entry.amountMinor) }),
      action: {
        label: t('quickAdd.undo'),
        onClick: () => {
          void store.transactions.restore(entry.id, me?.id ?? null).then(notifyDataChanged);
        },
      },
    });
  }

  return (
    <BottomSheet
      open
      onClose={close}
      title={label}
      footer={
        readOnly && activeSpace ? (
          <ReadOnlyNotice space={activeSpace} />
        ) : (
          <>
            <Button variant="primary" size="lg" block loading={saving} onClick={() => void save()}>
              {t('entry.save')}
            </Button>
            <Button variant="ghost" size="lg" block icon={Trash2} onClick={() => void remove()}>
              {t('entry.delete')}
            </Button>
          </>
        )
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!readOnly) void save();
        }}
      >
        <AmountInput
          label={t('quickAdd.amount')}
          value={text}
          currency={paidIn || entry.currency}
          inputRef={amountRef}
          onValueChange={(v, p) => {
            setText(v);
            setParsed(p);
            conversion.reset();
          }}
          onSubmit={() => void save()}
          error={amountError}
        />
        {conversion.foreign && <ConvertedAmount conversion={conversion} paidIn={paidIn} budgetCurrency={entry.currency} showError={conversionTouched} />}
        <p className="account-note" data-testid="entry-added-by">
          {addedBy}
        </p>
        <TextField label={t('quickAdd.date')} type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <PaidInField
          budgetCurrency={entry.currency}
          value={paidIn || entry.currency}
          onChange={(next) => {
            setPaidIn(next);
            setParsed(parseAmount(text, next));
            conversion.reset();
          }}
        />
        <PeopleFields members={members} paidBy={paidBy} belongsTo={belongsTo} onPaidBy={setPaidBy} onBelongsTo={setBelongsTo} />
        <TextField label={t('quickAdd.note')} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        {failed && (
          <p className="field-msg field-error" role="alert">
            {t('entry.error')}
          </p>
        )}
        {activeSpace && activeSpace.id !== entry.spaceId && <p className="account-note">{t('entry.otherBudget')}</p>}
      </form>
    </BottomSheet>
  );
}
